import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useStripe } from '../context/StripeContext';
import { toast } from 'sonner';

import {
  ArrowRight,
  Check,
  CircleNotch,
  Lightning,
  X
} from '@phosphor-icons/react';
const UpgradeModal = ({ 
  isOpen, 
  onClose, 
  currentTier = 'basic',
  message,
  feature,
  showComparison = true 
}) => {
  const navigate = useNavigate();
  const { createSubscriptionCheckout, loading: stripeLoading } = useStripe();
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradingPlan, setUpgradingPlan] = useState(null);

  useEffect(() => {
    const fetchPricing = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${BACKEND_URL}/api/pricing`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data && data.plans && data.plans.pro && data.plans.partner_pro) {
          setPricingData(data);
        } else {
          console.error('Invalid pricing data structure:', data);
        }
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchPricing();
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Format price display based on billing period
  const formatPrice = (plan) => {
    if (!pricingData || !pricingData.plans || !pricingData.plans[plan]) {
      // Fallback prices if data not loaded
      const fallbackPrices = {
        builder: { monthly: 29, yearly: 290, yearlyMonthly: 24.17 },
        pro: { monthly: 79, yearly: 790, yearlyMonthly: 65.83 },
        partner_pro: { monthly: 149, yearly: 1490, yearlyMonthly: 124.17 }
      };
      const fallback = fallbackPrices[plan];
      if (fallback) {
        if (billingPeriod === 'yearly') {
          return {
            display: `$${fallback.yearlyMonthly}`,
            period: '/mo',
            billedAs: `Billed $${fallback.yearly}/year`,
            savings: '16%',
            loading: false
          };
        }
        return {
          display: `$${fallback.monthly}`,
          period: '/month',
          loading: false
        };
      }
      return { display: null, period: '/month', loading: true };
    }
    
    const planData = pricingData.plans[plan];
    if (billingPeriod === 'yearly' && planData.yearly) {
      return {
        display: `$${planData.yearly.monthly_equivalent}`,
        period: '/mo',
        billedAs: `Billed $${planData.yearly.price}/year`,
        savings: planData.yearly.savings,
        loading: false
      };
    } else if (planData.monthly) {
      return {
        display: `$${planData.monthly.price}`,
        period: '/month',
        loading: false
      };
    }
    return { display: null, period: '', loading: true };
  };

  // Get tier info - returns loading state if pricing not yet loaded
  const getTierInfo = () => {
    const builderPrice = formatPrice('builder');
    const proPrice = formatPrice('pro');
    const partnerProPrice = formatPrice('partner_pro');
    
    return {
      basic: {
        name: 'Basic',
        limits: 'Assessment only - no agents'
      },
      builder: {
        name: 'Builder',
        price: builderPrice.display,
        period: builderPrice.period,
        billedAs: builderPrice.billedAs,
        loading: builderPrice.loading,
        limits: '10 deployed agents, 2,000 credits/mo',
        features: pricingData?.plans?.builder?.features || [
          'Everything in Basic',
          '10 assessments / month',
          'Full opportunity visibility',
          'Automation templates',
          'Export reports',
          'Integrations (CRM, email, webhooks, MCP)',
          'Advanced analytics',
          '10 deployed agents (2 Operator)',
          '1 Voice Agent',
          '2,000 monthly credits'
        ]
      },
      pro: {
        name: 'Pro',
        price: proPrice.display,
        period: proPrice.period,
        billedAs: proPrice.billedAs,
        loading: proPrice.loading,
        limits: 'Unlimited agents + voice agents',
        features: [
          'Unlimited assessments',
          'Full opportunity visibility',
          'Exportable reports (PDF)',
          'ROI & savings calculator',
          'Advanced analytics',
          'Integrations (CRM, email, webhooks, MCP)',
          'Full process & workflow automation',
          'Team members & seat management',
          'Unlimited agents, up to 3 Voice Agents',
          '5,000 monthly credits'
        ]
      },
      partner_pro: {
        name: 'Partner Pro',
        price: partnerProPrice.display,
        period: partnerProPrice.period,
        billedAs: partnerProPrice.billedAs,
        loading: partnerProPrice.loading,
        limits: 'Unlimited agents — multi-tenant',
        features: [
          'Everything in Pro, plus:',
          'Multi-tenant organizations',
          'Client / tenant seat & access management',
          'Tenant-level analytics',
          'White-label reports (logo, colors)',
          'Unlimited Voice Agents',
          '10,000 monthly credits'
        ]
      }
    };
  };

  const tierInfo = getTierInfo();

  const getUpgradeOptions = () => {
    const normalizedTier = currentTier?.replace('-', '_');
    if (normalizedTier === 'basic') {
      return ['builder', 'pro', 'partner_pro'];
    } else if (normalizedTier === 'builder') {
      return ['pro', 'partner_pro'];
    } else if (normalizedTier === 'pro') {
      return ['partner_pro'];
    }
    return [];
  };

  const upgradeOptions = getUpgradeOptions();

  const handleUpgrade = async (targetTier) => {
    // Trigger Stripe checkout directly
    const planId = `${targetTier}_${billingPeriod}`;
    setUpgradingPlan(targetTier);
    
    try {
      await createSubscriptionCheckout(planId);
      // User will be redirected to Stripe
    } catch (error) {
      console.error('Upgrade checkout error:', error);
      toast.error(error.message || 'Failed to start checkout. Please try again.');
      setUpgradingPlan(null);
    }
  };

  if (!isOpen) return null;

  // Use React Portal to render modal at document body level
  // This ensures the modal escapes any parent overflow/transform constraints
  const modalContent = (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-[#0D0F12] border border-[rgba(255,255,255,0.1)] rounded-3xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          >
            <X size={24} className="text-[#A0AEC0]" />
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] mb-4">
              <Lightning size={32} className="text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">
              {message || 'Upgrade to Unlock This Feature'}
            </h2>
            <p className="text-[#A0AEC0] mb-4">
              {feature ? `${feature} is available on higher tiers` : 'Choose a plan that fits your needs'}
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4">
              <span className={`text-sm ${billingPeriod === 'monthly' ? 'text-white font-semibold' : 'text-[#718096]'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
                className="relative w-14 h-7 rounded-full transition-colors"
                style={{ backgroundColor: billingPeriod === 'yearly' ? '#6366F1' : '#2D3748' }}
              >
                <div
                  className="absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform"
                  style={{ transform: billingPeriod === 'yearly' ? 'translateX(28px)' : 'translateX(0)' }}
                />
              </button>
              <span className={`text-sm ${billingPeriod === 'yearly' ? 'text-white font-semibold' : 'text-[#718096]'}`}>
                Yearly
              </span>
              {billingPeriod === 'yearly' && (
                <span className="px-2 py-1 rounded-full bg-gradient-to-r from-[#14F195] to-[#06B6D4] text-white text-xs font-semibold">
                  Save 16%
                </span>
              )}
            </div>
          </div>

          {/* Current Tier Info */}
          <div className="bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-white">Current Plan</h3>
              <span className="px-3 py-1 rounded-full bg-[#718096]/20 text-[#718096] text-sm font-medium">
                {tierInfo[currentTier]?.name || 'Basic'}
              </span>
            </div>
            <p className="text-[#A0AEC0] text-sm">
              {tierInfo[currentTier]?.limits || 'Limited access'}
            </p>
          </div>

          {/* Upgrade Options */}
          {(loading || !pricingData) && (
            <div className="text-center py-8">
              <div className="inline-block w-8 h-8 border-4 border-[#6366F1] border-t-transparent rounded-full animate-spin"></div>
              <p className="text-[#A0AEC0] mt-4">Loading pricing...</p>
            </div>
          )}
          {!loading && pricingData && showComparison && upgradeOptions.length > 0 && (
            <div className={`grid gap-6 ${upgradeOptions.length === 3 ? 'md:grid-cols-3' : upgradeOptions.length === 2 ? 'md:grid-cols-2' : 'max-w-md mx-auto'}`}>
              {upgradeOptions.map((tier) => (
                <div
                  key={tier}
                  className="flex flex-col bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-6 hover:bg-[rgba(255,255,255,0.05)] hover:border-[rgba(99,102,241,0.3)] transition-all"
                >
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold text-white mb-1">
                      {tierInfo[tier].name}
                    </h3>
                    <div>
                      <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#14F195] to-[#6366F1]">
                        {tierInfo[tier].loading ? (
                          <span className="inline-block w-20 h-8 bg-[rgba(255,255,255,0.1)] rounded animate-pulse"></span>
                        ) : (
                          <>{tierInfo[tier].price}{tierInfo[tier].period}</>
                        )}
                      </p>
                      {tierInfo[tier].billedAs && (
                        <p className="text-xs text-[#14F195] mt-1">
                          {tierInfo[tier].billedAs}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 flex-grow">
                    {tierInfo[tier].features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check size={18} className="text-[#14F195] flex-shrink-0 mt-0.5" />
                        <span className="text-[#A0AEC0] text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => handleUpgrade(tier)}
                    disabled={stripeLoading || upgradingPlan}
                    className="w-full relative overflow-hidden rounded-full group mt-6 disabled:opacity-50"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-[#14F195] to-[#6366F1]"></div>
                    <span className="relative z-10 flex items-center justify-center gap-2 py-3 text-white font-bold">
                      {upgradingPlan === tier ? (
                        <>
                          <CircleNotch size={20} className="animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Upgrade to {tierInfo[tier].name}
                          <ArrowRight size={20} />
                        </>
                      )}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Simple CTA for no options */}
          {upgradeOptions.length === 0 && (
            <div className="text-center">
              <p className="text-[#A0AEC0] mb-6">
                You&apos;re already on the highest tier!
              </p>
              <button
                onClick={onClose}
                className="px-6 py-3 rounded-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );

  // Render modal at document body level using React Portal
  return createPortal(modalContent, document.body);
};

export default UpgradeModal;
